<template>
  <div class="album-card">

    <!-- Header -->
    <div class="album-header" @click="toggleTracks">
      <!-- Album Cover Image -->
      <img
        class="album-image"
        :src="pictureUrl"
        :alt="album.album"
      />

      <div class="album-meta">
        <div class="album-title">
          <router-link :to="`/album/${album.albumid}`">
            {{ album.album }}
          </router-link>
        </div>

        <!-- Organist/Artist Info with Photo -->
        <div v-if="album.artist" class="album-artist">
          <img
            v-if="album.artistpicture"
            class="artist-photo"
            :src="`https://pictures.organlive.com/artists/${album.artistpicture}`"
            :alt="album.artist"
            @error="handleArtistPhotoError"
          />
          <router-link
            v-if="album.artistid"
            :to="`/artist/${album.artistid}`"
            class="artist-name"
          >
            {{ album.artist }}
          </router-link>
          <span v-else class="artist-name">{{ album.artist }}</span>
        </div>

        <div class="album-year">{{ album.albumyear }}</div>
        <div class="album-label">{{ album.label }} {{ album.catalog }}</div>
      </div>

      <button class="expand-btn" @click.stop="toggleTracks">
        {{ showTracks ? '▲' : '▼' }}
      </button>
    </div>

    <!-- Track list -->
    <transition name="fade">
      <div v-if="showTracks" class="track-list">

        <!-- Loading indicator -->
        <div v-if="isLoading" class="track-loading">
          Loading tracks<span class="dots"></span>
        </div>

        <!-- No tracks -->
        <div v-else-if="tracks.length === 0" class="no-tracks">
          No tracks found for this album with the current filters.
        </div>

        <!-- Tracks -->
        <div
          v-else
          v-for="track in tracks"
          :key="track.trackid"
          class="track-row"
        >
          <div class="track-number">{{ track.tracknum }}</div>

          <div class="track-info">
            <!-- Track Title -->
            <div class="track-title">{{ track.tracktitle }}</div>

            <!-- Star Rating Display (directly under title) -->
            <StarRating
              :model-value="getTrackRatingStats(track).averageRating || 0"
              :read-only="true"
              :compact="true"
              class="track-rating-display"
            />

            <!-- Track Meta -->
            <div class="track-meta">
              <span v-if="track.composer && !composerid">
                <router-link :to="`/composer/${track.composerid}`" class="track-meta-link">
                  {{ track.composer }}
                </router-link>
              </span>
              <span v-if="track.artist && !artistid">
                <span v-if="track.composer && !composerid"> • </span>
                <router-link :to="`/artist/${track.artistid}`" class="track-meta-link">
                  {{ track.artist }}
                </router-link>
              </span>
            </div>

            <!-- Organ Information -->
            <div v-if="trackOrgans[track.organid]" class="track-organ">
              <div class="track-organ-location">
                {{ formatOrganLocation(trackOrgans[track.organid]) }}
              </div>
              <div class="track-organ-details">
                <a
                  v-if="trackOrgans[track.organid].link"
                  :href="trackOrgans[track.organid].link"
                  class="organ-link"
                  target="_blank"
                  rel="noopener"
                >
                  {{ formatOrganSpecs(trackOrgans[track.organid]) }}
                </a>
                <span v-else>{{ formatOrganSpecs(trackOrgans[track.organid]) }}</span>
                <span v-if="trackOrgans[track.organid].glat && trackOrgans[track.organid].glong">
                  -
                  <a
                    :href="`https://maps.google.com/?q=${trackOrgans[track.organid].glat},${trackOrgans[track.organid].glong}`"
                    class="organ-map-link"
                    target="_blank"
                    rel="noopener"
                  >
                    Map
                  </a>
                </span>
              </div>
            </div>
          </div>

          <div class="track-links">
            <PdfLink v-if="track.PDFsheetmusic" :url="track.PDFsheetmusic" />
            <BuySheetMusicLink v-if="track.buy" :url="track.buy" />
            <RequestButton v-if="track.songid" :trackId="track.songid" />
          </div>
        </div>

      </div>
    </transition>

  </div>
</template>

<script>
import PdfLink from './PdfLink.vue'
import BuySheetMusicLink from './BuySheetMusicLink.vue'
import RequestButton from './RequestButton.vue'
import StarRating from './StarRating.vue'
import { getTrackRating } from '../utils/ratingUtils.js'
import { isSignedIn } from '../utils/authUtils.js'
import { onRatingUpdate } from '../utils/ratingEvents.js'

export default {
  name: 'AlbumCard',

  components: {
    PdfLink,
    BuySheetMusicLink,
    RequestButton,
    StarRating
  },

  props: {
    album: { type: Object, required: true },
    artistid: { type: String, default: '' },
    composerid: { type: String, default: '' },
    titlefilter: { type: String, default: '' },
    organistfilter: { type: String, default: '' },
    composerfilter: { type: String, default: '' }
  },

  data () {
    return {
      showTracks: false,
      tracks: [],
      trackOrgans: {},
      isLoading: false,
      userRatingsCache: {}, // Cache user ratings for performance
      ratingUnsubscribe: null // Unsubscribe function for rating events
    }
  },

  computed: {
    pictureUrl () {
      if (!this.album.picture) {
        return 'https://pictures.organlive.com/large/placeholder.png'
      }
      return `https://pictures.organlive.com/large/${this.album.picture}`
    }
  },

  mounted () {
    console.log('🚀🚀🚀 AlbumCard MOUNTED - Rating system active! 🚀🚀🚀')
    console.log('Album:', this.album.album, 'ID:', this.album.albumid)

    // Listen for global rating updates
    this.ratingUnsubscribe = onRatingUpdate((data) => {
      this.handleRatingUpdate(data)
    })
  },

  beforeUnmount () {
    // Clean up rating event listener
    if (this.ratingUnsubscribe) {
      this.ratingUnsubscribe()
    }
  },

  methods: {
    toggleTracks () {
      this.showTracks = !this.showTracks
      if (this.showTracks && this.tracks.length === 0) {
        this.loadTracks()
      }
    },

    async loadTracks () {
      this.isLoading = true
      this.tracks = []

      const params = []

      // Use filter parameters for search results, otherwise use context IDs
      if (this.organistfilter) {
        params.push(`artist=${encodeURIComponent(this.organistfilter)}`)
      } else if (this.artistid) {
        params.push(`artistid=${this.artistid}`)
      }

      if (this.composerfilter) {
        params.push(`composer=${encodeURIComponent(this.composerfilter)}`)
      } else if (this.composerid) {
        params.push(`composerid=${this.composerid}`)
      }

      if (this.titlefilter) {
        params.push(`title=${encodeURIComponent(this.titlefilter)}`)
      }

      const query = params.length ? `?${params.join('&')}` : ''

      const url = `https://api.organlive.com/library/tracks/${this.album.albumid}${query}`

      console.log('🔍 Fetching tracks from:', url)

      try {
        const response = await fetch(url)
        const json = await response.json()

        console.log('📦 Raw API response:', json)

        // Handle different response formats
        let tracks = []
        if (Array.isArray(json)) {
          // Already an array
          tracks = json
        } else if (json.tracks && Array.isArray(json.tracks)) {
          // Nested under 'tracks' key
          tracks = json.tracks
        } else if (typeof json === 'object' && json !== null) {
          // Object with numeric keys: {0: track, 1: track}
          tracks = Object.values(json)
        }

        console.log('📊 Converted to array:', tracks.length, 'tracks')

        // CRITICAL: Convert rating strings to numbers
        tracks = tracks.map(track => {
          const rating = track.rating ? parseFloat(track.rating) : 0
          console.log(`  Track "${track.tracktitle}" rating: "${track.rating}" → ${rating}`)
          return {
            ...track,
            rating
          }
        })

        this.tracks = tracks

        console.log('✅ Final tracks array:', this.tracks.length)
        console.log('✅ Sample track:', this.tracks[0])
        console.log('✅ Tracks with ratings > 0:', this.tracks.filter(t => t.rating > 0).length)

        // Load organ data for the tracks
        await this.loadTrackOrgans()

        // Load user ratings if signed in
        if (isSignedIn()) {
          await this.loadUserRatings()
        }
      } catch (err) {
        console.error('❌ Track load error:', err)
        this.tracks = []
      } finally {
        this.isLoading = false
      }
    },

    async loadTrackOrgans () {
      // Get unique organ IDs from tracks (excluding 9999 and invalid IDs)
      const organIds = [...new Set(
        this.tracks
          .map(t => t.organid)
          .filter(id => id && id !== '9999' && id !== '' && id !== null)
      )]

      if (organIds.length === 0) {
        return
      }

      // Fetch organ data for each unique organID
      const organPromises = organIds.map(async (organId) => {
        try {
          const response = await fetch(`https://api.organlive.com/library/organ/${organId}`)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const json = await response.json()
          return { organId: String(organId), data: json }
        } catch (err) {
          console.error(`Organ load error for ${organId}:`, err)
          return { organId: String(organId), data: null }
        }
      })

      const results = await Promise.all(organPromises)

      // Build new trackOrgans object
      const newTrackOrgans = {}
      results.forEach(({ organId, data }) => {
        if (data) {
          newTrackOrgans[String(organId)] = data
        }
      })

      // Reassign entire object to trigger Vue reactivity
      this.trackOrgans = newTrackOrgans
    },

    async loadUserRatings () {
      // Load user ratings for all tracks in this album
      if (!isSignedIn()) {
        return
      }

      const trackIds = this.tracks
        .map(t => t.songid || t.trackid)
        .filter(id => id)

      // Load ratings for each track
      const ratingsPromises = trackIds.map(async (trackId) => {
        try {
          const rating = await getTrackRating(trackId)
          return { trackId, rating }
        } catch (err) {
          console.error(`Error loading rating for track ${trackId}:`, err)
          return { trackId, rating: 0 }
        }
      })

      const ratings = await Promise.all(ratingsPromises)

      // Build cache
      const cache = {}
      ratings.forEach(({ trackId, rating }) => {
        cache[String(trackId)] = rating
      })

      this.userRatingsCache = cache
    },

    formatOrganLocation (organ) {
      if (!organ) return ''
      const parts = []
      if (organ.institution) parts.push(organ.institution)
      if (organ.city) parts.push(organ.city)
      if (organ.state) parts.push(organ.state)
      if (organ.country) parts.push(organ.country)
      return parts.join(', ')
    },

    formatOrganSpecs (organ) {
      if (!organ) return ''
      const parts = []
      if (organ.datebuilt) parts.push(organ.datebuilt)
      if (organ.manuals || organ.ranks) {
        parts.push(`${organ.manuals || '?'}/${organ.ranks || '?'}`)
      }
      if (organ.builder) parts.push(organ.builder)
      if (organ.rebuilder) parts.push(organ.rebuilder)
      return parts.join(' ')
    },

    /**
     * Get rating statistics for a track
     * Returns object with userRating, averageRating, displayRating
     */
    getTrackRatingStats (track) {
      const trackId = track.songid || track.trackid || ''

      // Get user rating from cache (if signed in)
      const userRating = this.userRatingsCache[String(trackId)] || 0

      // Get average rating from track data (from API)
      // IMPORTANT: API returns rating as STRING, convert to number
      let averageRating = 0
      if (track.rating) {
        averageRating = parseFloat(track.rating)
        if (isNaN(averageRating)) {
          averageRating = 0
        }
      }

      return {
        userRating,
        averageRating,
        displayRating: userRating > 0 ? userRating : averageRating,
        hasUserRated: userRating > 0
      }
    },

    /**
     * Determine if rating should be shown for a track
     * Show if: track has average rating OR user has rated it
     */
    shouldShowRating (track) {
      const stats = this.getTrackRatingStats(track)
      return stats.averageRating > 0 || stats.userRating > 0
    },

    /**
     * Handle global rating update event
     * Updates track rating if it's in the current track list
     */
    handleRatingUpdate (data) {
      const { trackId, userRating, averageRating } = data

      console.log('🔔 AlbumCard received rating update:', {
        trackId,
        userRating,
        averageRating,
        albumId: this.album.albumid
      })

      // Update user rating cache
      this.userRatingsCache[String(trackId)] = userRating

      // Find and update the track in the list
      const track = this.tracks.find(t =>
        String(t.songid) === String(trackId) ||
        String(t.trackid) === String(trackId)
      )

      if (track) {
        // Update track's average rating
        track.rating = averageRating
        console.log('✓ Updated track rating in AlbumCard:', trackId, averageRating)

        // Force re-render by creating new array reference
        this.tracks = [...this.tracks]
      }
    },

    /**
     * Handle artist photo load error - hide image if it fails
     */
    handleArtistPhotoError (event) {
      event.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.album-card {
  background: #333;
  color: #fff;
  padding: 1rem;
  margin-bottom: 1.2rem;
  border-radius: 10px;
  border: 1px solid #444;
}

/* Header layout */
.album-header {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.album-image {
  width: 75px;
  height: 75px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 1rem;
}

.album-meta {
  flex-grow: 1;
}

.album-title a {
  color: #9cf;
  font-size: 1.1rem;
  font-weight: bold;
  text-decoration: none;
}

.album-title a:hover {
  text-decoration: underline;
}

/* Artist info with photo */
.album-artist {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.3rem;
  margin-bottom: 0.3rem;
}

.artist-photo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #555;
}

.artist-name {
  color: #ccc;
  text-decoration: none;
  font-size: 0.95rem;
}

.artist-name:hover {
  color: #9cf;
  text-decoration: underline;
}

.album-year,
.album-label {
  font-size: 0.9rem;
  color: #ccc;
}

/* Expand button */
.expand-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 1.4rem;
  cursor: pointer;
}

/* Track list */
.track-list {
  margin-top: 0.8rem;
  border-top: 1px solid #444;
  padding-top: 0.8rem;
}

/* Loading */
.track-loading {
  font-size: 1rem;
  color: #ccc;
  padding: 0.4rem 0;
}

.dots::after {
  content: '...';
  animation: pulse 1s infinite;
  opacity: 0;
}

@keyframes pulse {
  0% { opacity: 0 }
  50% { opacity: 1 }
  100% { opacity: 0 }
}

/* No tracks message */
.no-tracks {
  color: #aaa;
  padding: 0.4rem 0;
}

/* Track rows */
.track-row {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin: 0.4rem 0;
  padding: 0.5rem 0;
  border-bottom: 1px solid #444;
}

.track-number {
  width: 2rem;
  font-weight: bold;
  color: #bbb;
  flex-shrink: 0;
}

.track-info {
  flex-grow: 1;
  min-width: 0;
}

/* Track title with rating */
.track-title-with-rating {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.2rem;
  flex-wrap: wrap;
}

.track-title {
  color: #fff;
  font-weight: 500;
  flex: 1;
  min-width: 0;
}

.track-rating-display {
  flex-shrink: 0;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  display: inline-block;
}

.track-rating-display ::v-deep .star-rating {
  display: flex;
  gap: 2px;
}

.track-meta {
  font-size: 0.85rem;
  color: #aaa;
}

.track-meta-link {
  color: #9cf;
  text-decoration: none;
}

.track-meta-link:hover {
  text-decoration: underline;
}

.track-links {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

/* Track Organ Info */
.track-organ {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #2a2a2a;
  border-radius: 4px;
  font-size: 0.85rem;
  line-height: 1.5;
}

.track-organ-location {
  color: #ddd;
  margin-bottom: 0.3rem;
}

.track-organ-details {
  color: #aaa;
}

.organ-link,
.organ-map-link {
  color: #9cf;
  text-decoration: none;
}

.organ-link:hover,
.organ-map-link:hover {
  text-decoration: underline;
}

/* Fade transition for track list */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .track-title-with-rating {
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
  .album-image {
    width: 60px;
    height: 60px;
  }

  .album-title a {
    font-size: 1rem;
  }

  .track-row {
    gap: 0.5rem;
  }

  .track-number {
    width: 1.5rem;
    font-size: 0.9rem;
  }

  .track-title-with-rating {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
  }

  .track-title {
    font-size: 0.95rem;
  }

  .track-meta {
    font-size: 0.8rem;
  }
}
</style>
